import { useEffect, useState } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FaFileExcel, FaFilePdf } from 'react-icons/fa'
import XLSX from 'xlsx-js-style'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

import useFetchData from '../../hooks/UseFetchData'
import CustomButton from '../../shared/CustomButton'
import CustomTable from '../../shared/CustomTable'
import DatePicker from '../../shared/DatePicker'
import DropdownField from '../../shared/DropdownField'
import TextInput from '../../shared/TextInput'
import { fetchData } from '../../helpers/fetchData'
import { getDepartmentList } from '../../services/ApiMethods'
import { setList } from '../../redux/slices/AppSlice'
import { formatDateTime } from '../../utils/FormateDateTime'
import { AmiriRegBase64 } from '../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../assets/fonts/AmiriB'

const amount = value => Number(value || 0).toFixed(2)

const userName = row => {
  if (row?.name && row?.created_by) {
    return `${row.name} (${row.created_by})`
  }
  return row?.name || row?.created_by || '-'
}

const depositTotals = reports => ({
  deposit_amount: reports.reduce((sum, row) => sum + Number(row?.deposit_amount || 0), 0),
  deposit_vat: reports.reduce((sum, row) => sum + Number(row?.deposit_vat || 0), 0),
  total_amount: reports.reduce((sum, row) => sum + Number(row?.total_amount || 0), 0),
  count: reports.reduce((sum, row) => sum + Number(row?.count || 1), 0),
})

const withCreatorName = reports =>
  [...(reports || [])]
    .map(row => ({
      ...row,
      creator_display: userName(row),
    }))
    .sort((a, b) => a.creator_display.localeCompare(b.creator_display))

const summaryColumns = t => [
  {
    header: t('User Name'),
    body: row =>
      row?.isGrandTotal ? (
        <span className="font-bold">{t('Grand Total')}</span>
      ) : (
        userName(row)
      ),
    bodyCellClass: 'border !border-gray-300',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('Deposit Amount'),
    body: row => amount(row?.deposit_amount),
   // bodyCellClass: 'border !border-gray-300 text-right',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('Deposit VAT'),
    body: row => amount(row?.deposit_vat),
   // bodyCellClass: 'border !border-gray-300 text-right',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('Total Amount'),
    body: row => amount(row?.total_amount),
  //  bodyCellClass: 'border !border-gray-300 text-right',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('Count'),
    body: row => row?.count || 0,
  //  bodyCellClass: 'border !border-gray-300 text-right',
    headerCellClass: '!bg-gray-200',
  },
]

const detailColumns = t => [
  {
    header: t('Date & Time'),
    body: row => (row?.deposit_date ? formatDateTime(row.deposit_date) : formatDateTime(row?.created_at)),
    bodyCellClass: 'border !border-gray-300',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('Deposit Invoice No'),
    body: row => row?.deposit_invoice_number || row?.invoice_number || '-',
    bodyCellClass: 'border !border-gray-300',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('MRN'),
    body: row => row?.file_number || '-',
    bodyCellClass: 'border !border-gray-300',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('Patient Name'),
    body: row => row?.patient_name || '-',
    bodyCellClass: 'border !border-gray-300',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('Deposit Amount'),
    body: row => amount(row?.deposit_amount),
    bodyCellClass: 'border !border-gray-300 text-right',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('Deposit VAT'),
    body: row => amount(row?.deposit_vat),
    bodyCellClass: 'border !border-gray-300 text-right',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('Total Amount'),
    body: row => amount(row?.total_amount),
    bodyCellClass: 'border !border-gray-300 text-right',
    headerCellClass: '!bg-gray-200',
  },
  {
    header: t('Payment Method'),
    body: row => row?.payment_method || row?.payment_option || '-',
    bodyCellClass: 'border !border-gray-300',
    headerCellClass: '!bg-gray-200',
  },
]

function UserCollectionDepositReport({ reportType = 'summary' }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { list } = useSelector(state => state?.app)
  const auth = useSelector(state => state.auth)
  const { data: requestData, executeFetch, loading } = useFetchData()
  const [departmentList, setDepartmentList] = useState([])
  const [departmentLoading, setDepartmentLoading] = useState(false)
  const [payloadData, setPayloadData] = useState({
    end_date: moment().toDate(),
    start_date: moment().subtract(1, 'day').toDate(),
    created_by: '',
    deposit_invoice_number: '',
    department_id: '',
    type: reportType,
  })

  const rows = requestData?.grouped_reports || []
  const isSummary = reportType === 'summary'
  const detailRows = withCreatorName(rows)

  const requestReport = async (payload = payloadData) => {
    await executeFetch('POST', '/v3/reports/deposit-invoices', {
      ...payload,
      type: reportType,
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
    })
  }

  useEffect(() => {
    const fetchReceptionists = async () => {
      if (list?.receptionists?.length > 0) return
      const response = await fetchData('GET', '/v3/invoices/getCreators', {})
      if (response?.data) {
        const receptionists = response.data.map(receptionist => ({
          ...receptionist,
          label: receptionist?.staff_info?.user?.fullname
            ? `${receptionist.staff_info.user.fullname} (${receptionist.created_by})`
            : receptionist.created_by,
          employee_id: receptionist.created_by,
        }))
        dispatch(setList({ key: 'receptionists', value: receptionists }))
      }
    }
    fetchReceptionists()
  }, [dispatch, list?.receptionists])

  useEffect(() => {
    getDepartmentList(setDepartmentLoading, setDepartmentList)
  }, [])

  const handleChange = event => {
    const { name, value } = event.target || {}
    setPayloadData(prev => ({ ...prev, [name]: value }))
  }

  const resetFilters = async () => {
    const payload = {
      end_date: moment().toDate(),
      start_date: moment().subtract(1, 'day').toDate(),
      created_by: '',
      deposit_invoice_number: '',
      department_id: '',
      type: reportType,
    }
    setPayloadData(payload)
    await requestReport(payload)
  }

  const tableRows = isSummary
    ? [
        ...rows,
        ...(rows.length
          ? [
              {
                isGrandTotal: true,
                ...depositTotals(rows),
              },
            ]
          : []),
      ]
    : detailRows

  const detailGroupTotals = detailRows.reduce((groups, row) => {
    const key = row.creator_display
    if (!groups[key]) groups[key] = []
    groups[key].push(row)
    return groups
  }, {})

  const detailGroupHeader = row => {
    const creator = row?.creator_display || '-'
    return (
      <div className="text-14-dark !font-semibold">
        {creator}
      </div>
    )
  }

  const detailGroupFooter = row => {
    const creator = row?.creator_display || '-'
    const totals = depositTotals(detailGroupTotals[creator] || [])
    return (
      <>
        <td colSpan={4} className="!px-[0.35rem] !py-[0.6rem] border !border-gray-300 bg-gray-100 text-right font-bold text-[12px] text-gray-800">
          {t('Subtotal')}
        </td>
        <td className="!px-[0.35rem] !py-[0.6rem] border !border-gray-300 bg-gray-100 text-right font-bold text-[12px] text-gray-800">
          {amount(totals.deposit_amount)}
        </td>
        <td className="!px-[0.35rem] !py-[0.6rem] border !border-gray-300 bg-gray-100 text-right font-bold text-[12px] text-gray-800">
          {amount(totals.deposit_vat)}
        </td>
        <td className="!px-[0.35rem] !py-[0.6rem] border !border-gray-300 bg-gray-100 text-right font-bold text-[12px] text-gray-800">
          {amount(totals.total_amount)}
        </td>
        <td className="!px-[0.35rem] !py-[0.6rem] border !border-gray-300 bg-gray-100" />
      </>
    )
  }

  const exportToExcel = () => {
    const rowTypes = []
    const data = isSummary
      ? tableRows.map(row => {
          rowTypes.push(row?.isGrandTotal ? 'grand' : 'normal')
          return {
            [t('User Name')]: row?.isGrandTotal ? t('Grand Total') : userName(row),
            [t('Deposit Amount')]: amount(row?.deposit_amount),
            [t('Deposit VAT')]: amount(row?.deposit_vat),
            [t('Total Amount')]: amount(row?.total_amount),
            [t('Count')]: row?.count || 0,
          }
        })
      : Object.entries(detailGroupTotals).flatMap(([creator, creatorRows]) => {
          const totals = depositTotals(creatorRows)
          rowTypes.push('group')
          const groupRows = [{
            [t('Date & Time')]: creator,
            [t('Deposit Invoice No')]: '',
            [t('MRN')]: '',
            [t('Patient Name')]: '',
            [t('Deposit Amount')]: '',
            [t('Deposit VAT')]: '',
            [t('Total Amount')]: '',
            [t('Payment Method')]: '',
          }]

          creatorRows.forEach(row => {
            rowTypes.push('normal')
            groupRows.push({
              [t('Date & Time')]: row?.deposit_date ? formatDateTime(row.deposit_date) : formatDateTime(row?.created_at),
              [t('Deposit Invoice No')]: row?.deposit_invoice_number || row?.invoice_number || '-',
              [t('MRN')]: row?.file_number || '-',
              [t('Patient Name')]: row?.patient_name || '-',
              [t('Deposit Amount')]: amount(row?.deposit_amount),
              [t('Deposit VAT')]: amount(row?.deposit_vat),
              [t('Total Amount')]: amount(row?.total_amount),
              [t('Payment Method')]: row?.payment_method || row?.payment_option || '-',
            })
          })

          rowTypes.push('subtotal')
          groupRows.push({
            [t('Date & Time')]: t('Subtotal'),
            [t('Deposit Invoice No')]: '',
            [t('MRN')]: '',
            [t('Patient Name')]: '',
            [t('Deposit Amount')]: amount(totals.deposit_amount),
            [t('Deposit VAT')]: amount(totals.deposit_vat),
            [t('Total Amount')]: amount(totals.total_amount),
            [t('Payment Method')]: '',
          })

          return groupRows
        })
    const worksheet = XLSX.utils.json_to_sheet(data)
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1')
    const border = {
      top: { style: 'thin', color: { rgb: 'CBD5E1' } },
      bottom: { style: 'thin', color: { rgb: 'CBD5E1' } },
      left: { style: 'thin', color: { rgb: 'CBD5E1' } },
      right: { style: 'thin', color: { rgb: 'CBD5E1' } },
    }
    const headerStyle = {
      fill: { fgColor: { rgb: '1A365D' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 12 },
      alignment: { horizontal: 'center', vertical: 'center' },
      border,
    }
    const normalStyle = {
      alignment: { horizontal: 'center', vertical: 'center' },
      border,
    }
    const zebraStyle = {
      fill: { fgColor: { rgb: 'F8FAFC' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border,
    }
    const groupStyle = {
      fill: { fgColor: { rgb: 'DDEBFF' } },
      font: { color: { rgb: '1D4ED8' }, bold: true, sz: 12 },
      alignment: { horizontal: 'left', vertical: 'center' },
      border,
    }
    const subtotalStyle = {
      fill: { fgColor: { rgb: 'E5E7EB' } },
      font: { color: { rgb: '111827' }, bold: true },
      alignment: { horizontal: 'center', vertical: 'center' },
      border,
    }
    const grandStyle = {
      fill: { fgColor: { rgb: '1A365D' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true },
      alignment: { horizontal: 'center', vertical: 'center' },
      border,
    }

    for (let C = range.s.c; C <= range.e.c; C += 1) {
      const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C })
      if (worksheet[headerAddress]) worksheet[headerAddress].s = headerStyle
    }

    for (let R = range.s.r + 1; R <= range.e.r; R += 1) {
      const type = rowTypes[R - 1]
      for (let C = range.s.c; C <= range.e.c; C += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
        if (!worksheet[cellAddress]) continue
        if (type === 'grand') worksheet[cellAddress].s = grandStyle
        else if (type === 'group') worksheet[cellAddress].s = groupStyle
        else if (type === 'subtotal') worksheet[cellAddress].s = subtotalStyle
        else worksheet[cellAddress].s = R % 2 === 0 ? zebraStyle : normalStyle
      }
    }

    worksheet['!cols'] = Object.keys(data[0] || {}).map(key => ({
      wch: Math.max(16, Math.min(32, key.length + 8)),
    }))
    worksheet['!rows'] = new Array(data.length + 1).fill({ hpt: 22 })
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Deposit Collection')
    XLSX.writeFile(workbook, isSummary ? 'deposit-collection-summary-report.xlsx' : 'deposit-collection-detailed-report.xlsx')
  }

  const exportToPDF = () => {
    const doc = new jsPDF(isSummary ? 'portrait' : 'landscape')

    doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
    doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
    doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64)
    doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold')
    doc.setFont('Amiri', 'normal')

    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 5
    const usable = pageWidth - margin * 2
    const currentDate = moment().format('DD/MM/YYYY HH:mm')
    const startDate = moment(payloadData?.start_date).format('DD/MM/YYYY')
    const endDate = moment(payloadData?.end_date).format('DD/MM/YYYY')
    const selectedUser = list?.receptionists?.find(
      receptionist => receptionist.employee_id === payloadData?.created_by
    )
    const reportFor = selectedUser?.label || t('All Users')
    const reportTitle = isSummary
      ? t('Deposit Collection Summary Report')
      : t('Deposit Collection Detailed Report')
    const orgName = auth?.buisnessInfo?.name_en || auth?.buisnessInfo?.name_ar || t('Organization')
    const location = auth?.buisnessInfo?.address || ''
    const logo = auth?.buisnessInfo?.logo

    const drawHeader = () => {
      doc.setFillColor(26, 54, 93)
      doc.rect(0, 0, pageWidth, 24, 'F')
      if (logo) {
        try { doc.addImage(`data:image/png;base64,${logo}`, 'PNG', margin, 2, 16, 16) } catch (_) {}
      }
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(10)
      doc.setFont('Amiri', 'bold')
      doc.text(orgName, margin + 20, 9)
      doc.setFontSize(7)
      doc.setFont('Amiri', 'normal')
      if (location) doc.text(location, margin + 20, 15)
      doc.setFontSize(11)
      doc.setFont('Amiri', 'bold')
      doc.text(reportTitle, pageWidth - margin, 9, { align: 'right' })
      doc.setFontSize(7)
      doc.setFont('Amiri', 'normal')
      doc.text(`${t('Printed On')}: ${currentDate}`, pageWidth - margin, 15, { align: 'right' })
    }

    drawHeader()

    let y = 27
    doc.setTextColor(50, 50, 50)
    doc.setFontSize(7)
    doc.setFont('Amiri', 'normal')
    doc.text(`${t('Period')}: ${startDate} - ${endDate}`, margin, y)
    doc.text(`${t('Report For')}: ${reportFor}`, pageWidth - margin, y, { align: 'right' })
    doc.setDrawColor(200, 200, 200)
    doc.setLineWidth(0.3)
    doc.line(margin, y + 2.5, pageWidth - margin, y + 2.5)
    y += 6

    const head = isSummary
      ? [[t('User Name'), t('Deposit Amount'), t('Deposit VAT'), t('Total Amount'), t('Count')]]
      : [[
          t('Date'),
          t('Deposit Invoice'),
          t('MRN'),
          t('Patient'),
          t('Deposit Amount'),
          t('Deposit VAT'),
          t('Total Amount'),
          t('Method'),
        ]]

    const gtStyle = { fontStyle: 'bold', fillColor: [26, 54, 93], textColor: [255, 255, 255] }
    const subtotalStyle = { fontStyle: 'bold', fillColor: [214, 234, 248], textColor: [13, 71, 161], halign: 'right' }
    const subtotalLabelStyle = { fontStyle: 'bold', fillColor: [214, 234, 248], textColor: [13, 71, 161], halign: 'left' }

    const summaryTotals = depositTotals(rows)
    const body = isSummary
      ? [
          ...rows.map(row => [
            userName(row),
            amount(row?.deposit_amount),
            amount(row?.deposit_vat),
            amount(row?.total_amount),
            row?.count || 0,
          ]),
          [
            { content: t('Grand Total'), styles: gtStyle },
            { content: amount(summaryTotals.deposit_amount), styles: gtStyle },
            { content: amount(summaryTotals.deposit_vat), styles: gtStyle },
            { content: amount(summaryTotals.total_amount), styles: gtStyle },
            { content: summaryTotals.count, styles: gtStyle },
          ],
        ]
      : Object.entries(detailGroupTotals).flatMap(([creator, creatorRows]) => {
          const totals = depositTotals(creatorRows)
          return [
            [{
              content: creator,
              colSpan: 8,
              styles: {
                fontStyle: 'bold',
                fillColor: [26, 54, 93],
                textColor: [255, 255, 255],
                halign: 'left',
              },
            }],
            ...creatorRows.map(row => [
              row?.deposit_date ? moment(row.deposit_date).format('DD/MM HH:mm') : row?.created_at ? moment(row.created_at).format('DD/MM HH:mm') : '-',
              row?.deposit_invoice_number || row?.invoice_number || '-',
              row?.file_number || '-',
              row?.patient_name || '-',
              amount(row?.deposit_amount),
              amount(row?.deposit_vat),
              amount(row?.total_amount),
              row?.payment_method || row?.payment_option || '-',
            ]),
            [
              { content: t('Subtotal'), colSpan: 4, styles: subtotalLabelStyle },
              { content: amount(totals.deposit_amount), styles: subtotalStyle },
              { content: amount(totals.deposit_vat), styles: subtotalStyle },
              { content: amount(totals.total_amount), styles: subtotalStyle },
              { content: '', styles: { fillColor: [214, 234, 248] } },
            ],
          ]
        })

    const columnStyles = isSummary
      ? {
          0: { cellWidth: 70, halign: 'left' },
          1: { cellWidth: 35, halign: 'right' },
          2: { cellWidth: 35, halign: 'right' },
          3: { cellWidth: 35, halign: 'right' },
          4: { cellWidth: 25, halign: 'right' },
        }
      : {
          0: { cellWidth: 25, halign: 'left' },
          1: { cellWidth: 45, halign: 'left' },
          2: { cellWidth: 20, halign: 'left' },
          3: { cellWidth: 55, halign: 'left' },
          4: { cellWidth: 32, halign: 'right' },
          5: { cellWidth: 30, halign: 'right' },
          6: { cellWidth: 34, halign: 'right' },
          7: { cellWidth: 46, halign: 'left' },
        }

    autoTable(doc, {
      startY: y,
      head,
      body,
      theme: 'grid',
      margin: { left: margin, right: margin, top: 28 },
      tableWidth: usable,
      styles: {
        fontSize: isSummary ? 8 : 7,
        cellPadding: { top: 1.8, right: 1.5, bottom: 1.8, left: 1.5 },
        lineColor: [180, 180, 180],
        lineWidth: 0.2,
        font: 'Amiri',
        overflow: 'linebreak',
        textColor: [20, 20, 20],
        minCellHeight: 6,
      },
      headStyles: {
        fillColor: [26, 54, 93],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        font: 'Amiri',
        fontSize: isSummary ? 8 : 7,
        valign: 'middle',
      },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      bodyStyles: {
        fillColor: [255, 255, 255],
        font: 'Amiri',
        fontSize: isSummary ? 8 : 7,
      },
      columnStyles,
      showHead: 'everyPage',
      didDrawPage: () => {
        drawHeader()
        const pageCount = doc.internal.getNumberOfPages()
        doc.setFontSize(7)
        doc.setTextColor(130, 130, 130)
        doc.text(
          `${t('Page')} ${doc.internal.getCurrentPageInfo().pageNumber} ${t('of')} ${pageCount}`,
          pageWidth / 2,
          pageHeight - 4,
          { align: 'center' }
        )
      },
    })
    doc.save(isSummary ? 'deposit-collection-summary-report.pdf' : 'deposit-collection-detailed-report.pdf')
  }

  return (
    <div>
      <div className="flex items-center w-full justify-between gap-2 mb-2">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex  items-center gap-2">
            <DatePicker
              name="start_date"
              useFormik={false}
              className="mt-1"
              value={payloadData?.start_date}
              onChange={handleChange}
              placeholder={t('Selectstartdate')}
              format="YYYY-MM-DD"
            />

            <DatePicker
              name="end_date"
              useFormik={false}
              className="mt-1"
              value={payloadData?.end_date}
              onChange={handleChange}
              placeholder={t('Selectenddate')}
              format="YYYY-MM-DD"
            />

            <DropdownField
              parentClass="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-2"
              placeholder={`${t('Select')} ${t('receptionist')}`}
              className="!mb-0"
              name="created_by"
              filter
              useFormik={false}
              options={list.receptionists}
              optionLabel="label"
              optionValue="employee_id"
              value={payloadData?.created_by}
              onChange={handleChange}
            />

            <DropdownField
              parentClass="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-2"
              placeholder={`${t('Select')} ${t('Department')}`}
              className="!mb-0"
              name="department_id"
              filter
              useFormik={false}
              options={departmentList}
              optionLabel="name_en"
              optionValue="id"
              value={payloadData?.department_id}
              onChange={handleChange}
              loading={departmentLoading}
            />

            <TextInput
              name="deposit_invoice_number"
              value={payloadData?.deposit_invoice_number || ''}
              handleChange={handleChange}
              placeholder={t('Deposit Invoice No')}
              useFormik={false}
              parentClass="!mt-1"
              className="h-[35px] w-[200px] mb-0"
            />
            <div className=" flex gap-2">
              <CustomButton
                title={t('ApplyFilters')}
                buttonType="primary"
                icon="pi pi-filter"
                className="w-fit h-[35px]"
                onClick={() => requestReport()}
              />
              <CustomButton
                title={t('Reset')}
                buttonType="secondary"
                icon="pi pi-refresh"
                className="w-fit h-[35px]"
                onClick={resetFilters}
              />
              <CustomButton
                title={t('Export to Excel')}
                buttonType="success"
                icon={FaFileExcel}
                className="w-fit h-[35px]"
                onClick={exportToExcel}
              />
              <CustomButton
                title={t('Export to PDF')}
                buttonType="danger"
                icon={FaFilePdf}
                className="w-fit h-[35px]"
                onClick={exportToPDF}
              />
            </div>
          </div>
        </div>
      </div>

      <CustomTable
        data={tableRows}
        isLoading={loading}
        columns={isSummary ? summaryColumns(t) : detailColumns(t)}
       // tableStyle={{ minWidth: isSummary ? '50rem' : '64rem' }}
       showGridlines
        rowGroupMode={isSummary || loading ? undefined : 'subheader'}
        groupRowsBy={isSummary || loading ? undefined : 'creator_display'}
        sortMode={isSummary || loading ? undefined : 'single'}
        sortField={isSummary || loading ? undefined : 'creator_display'}
        sortOrder={isSummary || loading ? undefined : 1}
        rowGroupHeaderTemplate={isSummary || loading ? undefined : detailGroupHeader}
        rowGroupFooterTemplate={isSummary || loading ? undefined : detailGroupFooter}
      />
    </div>
  )
}

export default UserCollectionDepositReport
